// Billing rollup — pure READ-SIDE computation over service_assignments lines.
// Groups active client billing lines by (cost-group anchor, payer) and sums
// them for display. NOTHING here writes billing; nothing here is stored.
//
// Mixed invoice_to vocabulary is tolerated ('ACT-0019', 'TCS', NULL, ...).

import type { BillingRollupGroup, BuildingBillingLine } from './types';

const ACT_RE = /^ACT-\d+$/i;

export interface RollupMemberInfo {
  facility_id: string;
  account_id: string | null;
  account_name: string | null;
  management_fee_pct: number | null;
}

export function payerForLine(
  line: BuildingBillingLine,
  memberByFacility: Map<string, RollupMemberInfo>,
): { payer_account_id: string | null; payer_label: string } {
  if (line.billing_account_id && ACT_RE.test(line.billing_account_id)) {
    return { payer_account_id: line.billing_account_id, payer_label: line.billing_account_id };
  }
  if (line.invoice_to && ACT_RE.test(line.invoice_to.trim())) {
    return { payer_account_id: line.invoice_to.trim().toUpperCase(), payer_label: line.invoice_to.trim().toUpperCase() };
  }
  if (line.invoice_to && line.invoice_to.trim() !== '') {
    // Non-ACT label (e.g. 'TCS') — keep verbatim, no account id.
    return { payer_account_id: null, payer_label: line.invoice_to.trim() };
  }
  const member = line.facility_id ? memberByFacility.get(line.facility_id) : undefined;
  if (member?.account_id) {
    return { payer_account_id: member.account_id, payer_label: member.account_name ?? member.account_id };
  }
  return { payer_account_id: null, payer_label: 'Unassigned' };
}

export function computeBillingRollup(
  lines: BuildingBillingLine[],
  members: RollupMemberInfo[],
  memberFacilityIds: Set<string>,
  accountNameById?: Map<string, string>,
): BillingRollupGroup[] {
  const memberByFacility = new Map(members.map((m) => [m.facility_id, m]));
  const groups = new Map<string, BillingRollupGroup>();

  for (const line of lines) {
    if (line.assignment_status !== 'Active') continue;
    const anchor = line.cost_group_facility_id ?? line.facility_id;
    if (!anchor) continue;
    const { payer_account_id, payer_label } = payerForLine(line, memberByFacility);
    const key = `${anchor}::${payer_account_id ?? payer_label}`;

    let group = groups.get(key);
    if (!group) {
      group = {
        anchor_facility_id: anchor,
        payer_account_id,
        payer_label:
          (payer_account_id && accountNameById?.get(payer_account_id)) ?? payer_label,
        lines: [],
        distinct_rates: [],
        management_fee_pcts: [],
        monthly_total: 0,
        spans_buildings: false,
      };
      groups.set(key, group);
    }
    group.lines.push(line);
    if (line.billing_rate != null && Number.isFinite(Number(line.billing_rate))) {
      group.monthly_total += Number(line.billing_rate);
    }
    if (line.facility_id && !memberFacilityIds.has(line.facility_id)) {
      group.spans_buildings = true;
    }
  }

  for (const group of groups.values()) {
    const rates = new Set<number>();
    const fees = new Set<number>();
    for (const line of group.lines) {
      if (line.billing_rate != null && Number.isFinite(Number(line.billing_rate))) {
        rates.add(Number(line.billing_rate));
      }
      const member = line.facility_id
        ? members.find((m) => m.facility_id === line.facility_id)
        : undefined;
      if (member?.management_fee_pct != null && Number(member.management_fee_pct) > 0) {
        fees.add(Number(member.management_fee_pct));
      }
    }
    group.distinct_rates = [...rates].sort((a, b) => a - b);
    group.management_fee_pcts = [...fees].sort((a, b) => a - b);
    // Cents-safe display total
    group.monthly_total = Math.round(group.monthly_total * 100) / 100;
  }

  // Biggest bill first — the master/portfolio group leads the panel.
  return [...groups.values()].sort((a, b) => b.monthly_total - a.monthly_total);
}

/**
 * "Building total (running lines)" — the members' own Active lines, summed.
 * A line belonging to another building that shares this building's billing
 * group is left out: the box shows the group's total, this shows the building's.
 * Active-only, the same rule as every billing rollup here (Ben, 2026-09-17).
 */
export function buildingRunningTotal(
  lines: BuildingBillingLine[],
  memberFacilityIds: Set<string>,
): number {
  let total = 0;
  for (const line of lines) {
    if (line.assignment_status !== 'Active') continue;
    if (!line.facility_id || !memberFacilityIds.has(line.facility_id)) continue;
    const n = Number(line.billing_rate);
    if (line.billing_rate != null && Number.isFinite(n)) total += n;
  }
  return Math.round(total * 100) / 100;
}

/**
 * Does any invoice actually cover two or more facilities? Only then does the
 * card say "Billed together" (Ben, 2026-10-19, item 552). Two lines on ONE
 * facility (a main clean and a day porter) are still that facility's own bill.
 */
export function hasSharedInvoice(groups: BillingRollupGroup[]): boolean {
  return groups.some(
    (g) => new Set(g.lines.map((l) => l.facility_id).filter(Boolean)).size > 1,
  );
}
